import React, { useState, useEffect } from "react";
import {
  CreateAndFilter,
  ContactsList,
  Container,
  Contact,
  CreateContactDiv
} from "./syles";
import { Button } from "../../components";
import { AuthContextType, useAuth } from "../../auth/auth-provider";
import Avatar from "../../omnichannel/avatar";
import { spaik } from "../../assets";
import PhoneMask from "../../components/phone-mask";
import * as comunCss from "../../components/comum_css";
import { Controller, useForm } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { zodResolver } from "@hookform/resolvers/zod";
import { v } from "../../util";
import useContacts from "../ContactsList/use-contacts";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  setShowModalContacts,
  setContactId,
  setNameContact,
  setSelectedContactAvatarUrl
} from "../../redux/slices/omnichannel";
import { useToast } from "../toast-provider";

interface IUserDataValidation {
  name: string;
  email: string;
  phone: string;
}

interface IContact {
  id: number;
  name: string | null;
  phone: string | null;
  avatarUrl?: string;
}

const Contacts = () => {
  const { user } = useAuth() as AuthContextType;
  const [showContacts, setShowContacts] = useState(true);
  const { ListContactsOmnichannel, CreateContact } = useContacts();
  const toast = useToast();
  const [contacts, setContacts] = useState<IContact[]>([]);
  const workspaces = user?.workspaces?.workspaces;
  const dispatch = useAppDispatch();
  const selectedWorkspacesIds = useAppSelector(
    (state) => state.workspaces.selectedWorkspaces
  );

  const [offset, setOffset] = useState(0);
  const limit = 20;
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");

  const [nameField, setNameField] = useState("");
  const [emailField, setEmailField] = useState("");
  const [phoneField, setPhoneField] = useState("");
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null>(
    null
  );
  const onChangeName = (name: string) => setNameField(name);
  const onChangeEmail = (email: string) => setEmailField(email);
  const onChangePhone = (phone: string) => setPhoneField(phone);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<IUserDataValidation>({
    defaultValues: {
      name: "",
      email: "",
      phone: ""
    },
    values: {
      name: nameField,
      email: emailField,
      phone: phoneField
    },
    resolver: zodResolver(
      v.object({
        name: v.string().superRefine(v.nonempty()),
        email: v.string(),
        phone: v.string().refine((value) => {
          if (!value) return false;

          if (value.startsWith("55")) {
            return /^\d{12,13}$/.test(value);
          }
          return /^\d{9,}$/.test(value);
        })
      })
    )
  });

  const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const timer = setTimeout(() => setDebouncedValue(value), delay);

      return () => {
        clearTimeout(timer);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  const debouncedSearch = useDebounce(search, 600);

  useEffect(() => {
    const loadContacts = async () => {
      try {
        if (
          !user.workspaces?.workspaces ||
          user.workspaces?.workspaces.length === 0
        )
          return;

        setLoading(true);
        const data = await ListContactsOmnichannel({
          offset: 0,
          limit,
          workspaces: user.workspaces.workspaces.map((ws) => ws.id),
          description: debouncedSearch
        });
        setContacts(data.result);
        setOffset(data.result.length);
        setHasMore(data.result.length === limit);
      } catch (error) {
        toast?.current?.show({
          life: 5000,
          severity: "error",
          summary: "Erro.",
          detail: "Erro ao listar os contatos."
        });
      } finally {
        setLoading(false);
      }
    };

    setOffset(0);
    setHasMore(true);

    loadContacts();
  }, [selectedWorkspacesIds, debouncedSearch, user.workspaces?.workspaces]);

  const loadMoreContacts = async () => {
    if (loading) return;
    if (!hasMore) return;
    if (
      !user.workspaces?.workspaces ||
      user.workspaces?.workspaces.length === 0
    )
      return;

    try {
      setLoading(true);

      const currentOffset = offset;

      const data = await ListContactsOmnichannel({
        offset: currentOffset,
        limit,
        workspaces: user.workspaces.workspaces.map((ws) => ws.id),
        description: debouncedSearch
      });

      setContacts((prev) => [...prev, ...data.result]);
      setOffset(currentOffset + data.result.length);

      if (data.result.length < limit) {
        setHasMore(false);
      }
    } catch (error) {
      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro.",
        detail: "Erro ao listar os contatos."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - scrollTop <= clientHeight + 10) {
      loadMoreContacts();
    }
  };

  const handleCreateContact = async () => {
    if (!selectedWorkspaceId) {
      toast?.current?.show({
        life: 5000,
        severity: "warn",
        summary: "Atenção",
        detail: "Selecione um workspace."
      });
      return;
    }

    try {
      const payload = {
        name: nameField.trim(),
        phone: phoneField.trim(),
        email: emailField.trim() === "" ? null : emailField.trim(),
        workspaceId: selectedWorkspaceId
      };

      const createdContact = await CreateContact(payload);

      toast?.current?.show({
        life: 5000,
        severity: "success",
        summary: "Sucesso",
        detail: "Contato criado com sucesso."
      });

      setNameField("");
      setEmailField("");
      setPhoneField("");
      setSelectedWorkspaceId(null);
      dispatch(setContactId(createdContact.id));
      dispatch(setNameContact(payload.name || "Sem nome"));
      dispatch(setSelectedContactAvatarUrl(createdContact.avatarUrl || ""));
      dispatch(setShowModalContacts(false));
    } catch (error: any) {
      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Erro ao criar o contato.";

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  const handleClickContact = (id: number) => {
    dispatch(setContactId(id));
    dispatch(setShowModalContacts(false));
  };

  return (
    <Container>
      <div className="header">
        <img
          src={user.plataform.urlLogo ? user.plataform.urlLogo : spaik}
          style={{ maxWidth: "60px", maxHeight: "60px" }}
        />
        <div>Nova sala</div>
      </div>
      {showContacts ? (
        <>
          <CreateAndFilter>
            <div className="divInput">
              <i className="pi pi-search" />

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar"
              />
            </div>

            <Button
              label="Criar contato"
              style={{
                display: "flex",
                width: "180px",
                borderRadius: "8px",
                backgroundColor: user.plataform.mainColor || "#2624CD",
                borderColor: user.plataform.mainColor || "#2624CD"
              }}
              onClick={() => setShowContacts(false)}
            />
          </CreateAndFilter>

          <ContactsList onScroll={handleScroll}>
            {contacts.map((contact) => (
              <Contact
                key={contact.id}
                onClick={() => {
                  handleClickContact(contact.id);
                  dispatch(setNameContact(contact.name || "Sem nome"));
                  dispatch(
                    setSelectedContactAvatarUrl(contact.avatarUrl || "")
                  );
                }}
              >
                {contact.avatarUrl ? (
                  <img
                    src={contact.avatarUrl}
                    alt={contact.name || "Sem nome"}
                    style={{
                      height: "45px",
                      width: "45px",
                      borderRadius: "50%",
                      objectFit: "cover"
                    }}
                  />
                ) : (
                  <Avatar name={contact.name || "S"} />
                )}
                <div>
                  <div className="name">{contact.name || "Sem nome"}</div>
                  <div className="phone">{contact.phone}</div>
                </div>
              </Contact>
            ))}
          </ContactsList>
        </>
      ) : (
        <>
          <CreateContactDiv>
            <div className="fields">
              <span className="field relative">
                <label
                  css={comunCss.inputLabelDefault}
                  htmlFor="userName"
                  className="block"
                >
                  Nome Completo
                </label>

                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      {...field}
                      className="w-full"
                      css={comunCss.noBorder}
                      id="userName"
                      value={nameField}
                      onChange={(e) => {
                        onChangeName(e.target.value);
                        field.onChange(e.target.value);
                      }}
                    />
                  )}
                />

                {errors.name?.message ? (
                  <div style={{ color: "#AA3939" }}>{errors.name.message}</div>
                ) : null}
              </span>

              <span className="field relative">
                <label css={comunCss.inputLabelDefault} htmlFor="userEmail">
                  E-mail
                </label>

                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      {...field}
                      className="w-full"
                      css={comunCss.noBorder}
                      id="userEmail"
                      value={emailField}
                      onChange={(e) => {
                        onChangeEmail(e.target.value);
                        field.onChange(e.target.value);
                      }}
                    />
                  )}
                />
              </span>

              <span className="field relative">
                <label css={comunCss.inputLabelDefault} htmlFor="userPhone">
                  Telefone
                </label>

                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <PhoneMask
                      {...field}
                      value={phoneField}
                      onChange={(e) => {
                        onChangePhone(e.target.value);
                        field.onChange(e.target.value);
                      }}
                      style={{
                        width: "100%",
                        borderRadius: "0px",
                        border: "1px solid #ced4da",
                        padding: "8px 12px",
                        color: "#6e6b7b",
                        fontSize: "12px"
                      }}
                    />
                  )}
                />
              </span>

              {errors.phone?.message ? (
                <div style={{ color: "#AA3939" }}>Campo Obrigatório</div>
              ) : null}

              <Dropdown
                style={{ width: "100%", marginTop: "1rem" }}
                value={selectedWorkspaceId}
                options={workspaces}
                onChange={(e) => setSelectedWorkspaceId(e.value)}
                optionLabel="name"
                optionValue="id"
                placeholder="Selecione um workspace"
                showClear={false}
                filter={true}
              />
            </div>

            <div className="buttons">
              <Button
                label="Salvar"
                style={{
                  display: "flex",
                  width: "150px",
                  borderRadius: "8px",
                  backgroundColor: user.plataform.mainColor || "#2624CD",
                  borderColor: user.plataform.mainColor || "#2624CD"
                }}
                onClick={handleSubmit(() => handleCreateContact())}
              />

              <Button
                label="Cancelar"
                style={{
                  display: "flex",
                  width: "150px",
                  borderRadius: "8px",
                  backgroundColor: "#d2d2d2",
                  borderColor: "#d2d2d2"
                }}
                onClick={() => {
                  setShowContacts(true);
                }}
              />
            </div>
          </CreateContactDiv>
        </>
      )}
    </Container>
  );
};

export default Contacts;
